import useDevice from 'hooks/useDevice';
import { getValueSafely } from 'utils/utils';
import { useRootStore, useUserRolePermissionStore } from 'contexts/RootStore.context';
import { Flyout } from 'components/Flyout/Flyout';
import { Asset } from 'types/asset';
import { assetNameStyle, assetTypeStyle, NoOperationSupprtStyle, SeperatorStyle, StyledTable } from './styles';
import { style } from 'typestyle';
import { ButtonStyledAsLink } from 'components/Button/styled';
import { Horizontal } from 'gls';
import { StretchSpacer } from 'gls';
import { format } from 'date-fns';
import { icons } from 'assets/icons/index'
import { Tab, Tabs } from 'components/Tabs';
import AssetActivityTabContent from './AssetActivityTab';
import AssetDetailsTab from './AssetDetailsTab';
import { Box } from 'gls';
import Button from 'components/Button/Button';
import ArchiveAssetModal from './ArchiveAssetModal';
import { Vertical } from 'gls';
import { useAssetFlyout } from './useAssetFlyout';
import AssetStatusCell from '../AssetList/components/AssetStatusCell';
import { UpdateStatusModal } from './UpdateStatusModal';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';

type AssetFlyoutProps = {
  onClose: () => void
  asset: Asset | null
  hideAssetActivity?: boolean
  onStatusUpdate: () => void
}

export const AssetFlyout = observer((props: AssetFlyoutProps) => {

  const { isMobileDevice } = useDevice();
  const { canManageAssets } = useUserRolePermissionStore()
  const { availableFeatures } = useRootStore()
  const navigate = useNavigate()

  const { onClose, asset, hideAssetActivity } = props
  const {
    archiveModal,
    setArchiveModal,
    handleArchiveAsset,
    loading,
    updateAssetStatus,
    statusModal,
    setStatusModal,
  } = useAssetFlyout(asset, onClose)

  return (
    <>
      <Flyout isOpen={asset !== null} onClose={onClose}
        headerTitle={isMobileDevice
          ? <Vertical spacing={0}>
            <span className={assetNameStyle}>{getValueSafely(asset?.asset_name)}</span>
            <span className={assetTypeStyle}>{asset?.asset_type.name}</span>
          </Vertical>
          : 'Asset Details'}
        footer={canManageAssets && (isMobileDevice
          ? (
            <Box horizontalAlign='center' className={NoOperationSupprtStyle}>
              Please use a larger screen to edit or archive assets.
            </Box>
          )
          : (
            <>
              <Button className="biggerButton" outline onClick={() => setArchiveModal(true)} id="archive-button">
                Archive
              </Button>
              <Button className="biggerButton" onClick={() => { navigate(`/edit-asset/${asset?.asset_id}`); }} id="edit-button">
                Edit
              </Button>
            </>
          ))}
      >
        {(availableFeatures.asset_status || !isMobileDevice) &&
          <>
            <StyledTable>
              <tbody>
                {!isMobileDevice && (
                  <>
                    <tr>
                      <td>Asset Name:</td>
                      <td>{getValueSafely(asset?.asset_name)}</td>
                    </tr>
                    <tr>
                      <td>Asset Type:</td>
                      <td>{getValueSafely(asset?.asset_type.name)}</td>
                    </tr>
                  </>
                )}
                {availableFeatures.asset_status && (
                  <tr>
                    <td>Asset Status:</td>
                    <td>
                      <Horizontal>
                        <AssetStatusCell value={asset?.status_assignment?.asset_status?.description} />
                        <StretchSpacer />
                        {
                          <ButtonStyledAsLink id="update-asset-button" onClick={updateAssetStatus}>
                            Update Asset Status
                          </ButtonStyledAsLink>
                        }
                      </Horizontal>
                    </td>
                  </tr>
                )}
              </tbody>
            </StyledTable>
            <div className={SeperatorStyle} />
          </>
        }
        <StyledTable>
          <tbody>
            <tr>
              <td>Asset ID:</td>
              <td>{getValueSafely(asset?.asset_id)}</td>
            </tr>
            <tr>
              <td>Added On:</td>
              <td>{asset?.creation_timestamp
                ? format(new Date(asset?.creation_timestamp), 'dd/LL/yyyy - HH:mm')
                : '––'}</td>
            </tr>
            <tr>
              <td>Owner:</td>
              <td>{asset?.owner.name}</td>
            </tr>
            <tr>
              <td valign="top">Description:</td>
              <td id="assetDetailsDescription">{getValueSafely(asset?.description)}</td>
            </tr>
          </tbody>
        </StyledTable>
        {!availableFeatures.asset_status && asset && (
          <>
            <div className={SeperatorStyle} />
            <AssetDetailsTab asset={asset} />
          </>
        )}
        {availableFeatures.asset_status && asset && hideAssetActivity &&
          <>
            <div className={SeperatorStyle} />
            <AssetDetailsTab asset={asset} />
          </>
        }
        {availableFeatures.asset_status && asset && !hideAssetActivity &&
          <Tabs expandTabs tabsClassName={style({ height: '75px' })}>
            <Tab tabId="assetDetails-tab"
              title={
                <Horizontal spacing={6}>
                  <icons.InfoIcon
                    aria-label='asset details'
                    className={style({ position: "relative", top: "1px" })}
                    width={16}
                    height={16}
                  />
                  <span>Asset Details</span>
                </Horizontal>
              }
            >
              <AssetDetailsTab asset={asset} />
            </Tab>
            <Tab tabId="assetActivity-tab"
              title={
                <Horizontal spacing={6}>
                  <icons.ActivityIcon
                    aria-label='asset Activity'
                    className={style({ position: "relative", top: "1px" })}
                    width={16}
                    height={16}
                  />
                  <span>Asset Activity</span>
                </Horizontal>
              }
            >
              <AssetActivityTabContent asset={asset} />
            </Tab>
          </Tabs>
        }
      </Flyout >

      {archiveModal && asset &&
        <ArchiveAssetModal
          asset={asset}
          loading={loading}
          onClose={() => setArchiveModal(false)}
          onArchive={handleArchiveAsset}
        />}

      {asset && statusModal &&
        <UpdateStatusModal
          asset={asset}
          onClose={() => setStatusModal(false)}
          onUpdate={() => {
            setStatusModal(false)
            props.onStatusUpdate()
          }}
        />
      }
    </>
  );
})
